import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, styled, useTheme } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AnimatePresence, motion } from 'framer-motion';
import { SButton, SRoundedContainer } from '@setvi/shared/components';
import { Customer } from 'Services/MongoRealm/Types';
import {
  PriceGroup,
  Product,
  ProductDetailsTabsIds
} from '@setvi/shared/interfaces';

import { RealmProvider } from 'Providers/MongoRealmProvider';
import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';
import { InvalidConfiguration } from 'pages/products/components/invalid-configuration';
import { CustomerPreferencesInterface } from 'componentsV2/customer-preferences/interfaces';

import { IntelligenceIcon } from '@setvi/shared/components/sicons';

import { Footer, Header, ProductAskAI, Tabs } from './components';
import {
  AssociatedProducts,
  AssociatedResource,
  OrderHistory,
  ProductInfo,
  SalesEnablement,
  SimilarProducts,
  Warehouse,
  ProductEnhancer
} from './subpages';
import { AskAiButton } from './components/ask-ai/styled';
import { useProductAskAi } from './components/ask-ai/hook/use-ask-ai';
import { useAskAIStorage } from './components/ask-ai/store';

const Container = styled(SRoundedContainer)({
  width: '100%',
  overflow: 'hidden auto'
});

const AnimatedBox = motion(Box);

export interface DetailsContentProps {
  product: Product;
  selectedProducts?: Product[];
  selectedCustomer?: Customer;
  defaultOpenedTab?: ProductDetailsTabsIds;
  client: any;
  canSelectProducts?: boolean;
  readOnly?: boolean;
  hideProductPrice?: boolean;
  orderHistoryValues?: {
    accountId: string;
    addressId: string;
  };
  selectedPriceGroup?: PriceGroup;
  customerPreferences?: CustomerPreferencesInterface;
  priceLoading?: boolean;
  disableStockRefresh?: boolean;
  handleProductSelect?: (isChecked: boolean, product: Product[]) => void;
  updateProduct?: (product: Product, props: Partial<Product>) => void;
  captureResourceAnalytics?: (additionalValues?: {
    [key: string]: string;
  }) => void;
  openProductDetails?: (
    product: Product,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => void;
  closeDialog?: () => void;
  handleEmail?: () => void;
  handleSelected?: () => void;
  handleCloseActionBar?: () => void;
}

export const DetailsContent = ({
  product,
  selectedProducts,
  selectedCustomer,
  defaultOpenedTab = ProductDetailsTabsIds.PRODUCT_INFO,
  client,
  canSelectProducts,
  priceLoading,
  hideProductPrice = false,
  readOnly = false,
  orderHistoryValues,
  customerPreferences,
  selectedPriceGroup,
  disableStockRefresh,
  handleProductSelect,
  updateProduct,
  captureResourceAnalytics,
  openProductDetails,
  closeDialog,
  handleEmail,
  handleSelected,
  handleCloseActionBar
}: DetailsContentProps) => {
  const flags = useFlags();
  const { spacing } = useTheme();

  const { openAskAi, setValue, setOpenAskAi } = useAskAIStorage();
  const [activeTab, setActiveTab] =
    useState<ProductDetailsTabsIds>(defaultOpenedTab);

  const { configuration, invalidConfiguration } = useCatalogConfig();

  const parentRef = useRef<HTMLDivElement>(null);
  const tabs = configuration.productDetails?.tabs || [];

  const tabStyle = useMemo(
    () => ({
      display: 'flex',
      overflow: 'hidden',
      padding: '24px',
      paddingTop: 8,
      flex: 1,
      paddingRight: openAskAi ? '12px' : '24px'
    }),
    [openAskAi]
  );

  const {
    questions,
    conversation,
    initLoading,
    loading,
    onSubmit,
    setConversation
  } = useProductAskAi({
    product,
    customerPreferences
  });

  useEffect(() => {
    setActiveTab(defaultOpenedTab);
  }, [defaultOpenedTab]);

  if (invalidConfiguration) return <InvalidConfiguration />;

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        minHeight={0}
        overflow="hidden auto">
        <Box px={4} py={3} display="flex" justifyContent="space-between">
          <Header
            product={product}
            selectedCustomer={selectedCustomer}
            selectedPriceGroup={selectedPriceGroup}
            hideProductPrice={hideProductPrice}
            client={client}
            priceLoading={priceLoading}
            pricing={configuration?.productDetails?.pricing}
            priceType={configuration.price}
            productDetails={configuration.productDetails}
            closeDetailsDialog={closeDialog}
            updateProduct={updateProduct}
            captureResourceAnalytics={captureResourceAnalytics}
            stockRefresh={
              disableStockRefresh
                ? false
                : configuration.productDetails.stock.refresh
            }
            onStockClick={() => setActiveTab(ProductDetailsTabsIds.WAREHOUSE)}
          />
        </Box>

        <Box display="flex" flex={1} overflow="hidden auto">
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            minHeight={0}
            overflow="hidden auto"
            // @ts-ignore
            ref={parentRef}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              overflow="auto"
              pr={4}>
              <Tabs
                currentTab={activeTab}
                tabs={tabs}
                onTabChange={setActiveTab}
                orderHistoryValues={!!orderHistoryValues}
              />

              {!openAskAi && flags?.productAssistant && (
                <AskAiButton>
                  <SButton
                    variant="outlined"
                    onClick={() => setOpenAskAi(true)}
                    startIcon={<IntelligenceIcon />}>
                    Ask AI
                  </SButton>
                </AskAiButton>
              )}
            </Box>

            {activeTab === ProductDetailsTabsIds.PRODUCT_INFO && (
              <Box style={tabStyle}>
                <ProductInfo product={product} client={client} />
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.WAREHOUSE && (
              <Box style={tabStyle}>
                <Container>
                  <Warehouse
                    product={product}
                    selectedCustomer={selectedCustomer}
                    configColumns={
                      configuration.productDetails.tabs.find(
                        i => i.id === ProductDetailsTabsIds.WAREHOUSE
                      )?.columns
                    }
                  />
                </Container>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.ASSOCIATED_RESOURCE && (
              <Box style={tabStyle}>
                <Container display="flex">
                  <AssociatedResource
                    product={product}
                    readOnly={readOnly}
                    captureResourceAnalytics={captureResourceAnalytics}
                    closeDialog={closeDialog}
                  />
                </Container>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.ASSOCIATED_PRODUCTS && (
              <RealmProvider>
                <Box style={tabStyle}>
                  <Container display="flex">
                    <AssociatedProducts
                      product={product}
                      selectedProducts={selectedProducts}
                      canSelectProducts={canSelectProducts}
                      readOnly={readOnly}
                      handleProductSelect={handleProductSelect}
                      openProductDetails={openProductDetails}
                      closeDialog={closeDialog}
                      handleEmail={handleEmail}
                      handleSelected={handleSelected}
                      handleCloseActionBar={handleCloseActionBar}
                    />
                  </Container>
                </Box>
              </RealmProvider>
            )}
            {activeTab === ProductDetailsTabsIds.SIMILAR_PRODUCTS && (
              <Box style={tabStyle}>
                <Container display="flex">
                  <SimilarProducts
                    product={product}
                    selectedProducts={selectedProducts}
                    canSelectProducts={canSelectProducts}
                    scrollParentRef={parentRef}
                    closeDialog={closeDialog}
                    readOnly={readOnly}
                    openProductDetails={val => {
                      setActiveTab(ProductDetailsTabsIds.PRODUCT_INFO);
                      openProductDetails(val);
                    }}
                    handleProductSelect={handleProductSelect}
                    selectedCustomer={selectedCustomer}
                    handleEmail={handleEmail}
                    handleSelected={handleSelected}
                    handleCloseActionBar={handleCloseActionBar}
                  />
                </Container>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.SALES_ENABLEMENT &&
              flags?.salesEnablement && (
                <Box style={tabStyle}>
                  <SalesEnablement product={product} />
                </Box>
              )}

            {activeTab === ProductDetailsTabsIds.ORDER_HISTORY && (
              <Box style={tabStyle}>
                <Container>
                  <OrderHistory
                    productId={product._id}
                    {...orderHistoryValues}
                  />
                </Container>
              </Box>
            )}

            {activeTab === ProductDetailsTabsIds.PRODUCT_ENHANCER &&
              flags?.productEnhancer && (
                <Box style={tabStyle}>
                  <ProductEnhancer product={product} />
                </Box>
              )}
          </Box>

          <AnimatePresence>
            {flags?.productAssistant && openAskAi && (
              <AnimatedBox
                key="ask-ai-box"
                initial={{ width: 0, opacity: 0, padding: 0 }}
                animate={{
                  width: 350,
                  opacity: 1,
                  padding: spacing(2, 4, 4, 0)
                }}
                exit={{ width: 0, opacity: 0, padding: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                style={{
                  overflow: 'hidden'
                }}>
                <ProductAskAI
                  onClose={() => {
                    setValue('');
                    setOpenAskAi(false);
                  }}
                  onSubmit={onSubmit}
                  conversation={conversation}
                  loading={loading}
                  product={product}
                  initLoading={initLoading}
                  questions={questions}
                  setConversation={setConversation}
                />
              </AnimatedBox>
            )}
          </AnimatePresence>
        </Box>

        <Footer
          product={product}
          readOnly={readOnly}
          selectedProducts={selectedProducts}
          handleProductSelect={handleProductSelect}
          canSelectProducts={canSelectProducts}
        />
      </Box>
    </>
  );
};
